import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
    useCallback,
    useEffect,
    useId,
    useRef,
    useState,
    type KeyboardEvent as ReactKeyboardEvent,
    type ReactNode
} from 'react';
import type { FilterOption } from '../components/filtering/MoreFiltersMenu';
import { FilterDropdownOptionList } from './FilterDropdownOptionList';
import {
    flattenFilterGroups,
    resolveFilterGroups,
    type ResolvedFilterSection
} from './filterDropdownSections';
import { dataTableFilterPillClasses } from './filterPillStyles';
import { cx } from './utils';

/** Above this many options the in-menu search box appears by default. */
const SEARCHABLE_FROM = 8;

/** Kept in step with the menu's `duration-300`, so content unmounts after it. */
const MENU_TRANSITION_MS = 300;

const MENU_OPEN_MAX_HEIGHT = '24rem';

/** Inline rather than a class because `as const` is required here - see note below. */
const PILL_MIN_WIDTH = { minWidth: '160px' } as const;

const ALIGNMENT: Record<'left' | 'center' | 'right', string> = {
    left: 'left-0',
    center: 'left-1/2 -translate-x-1/2',
    right: 'right-0'
};

/** Key of the synthetic section a flat (non-sectioned) dropdown resolves into. */
const FLAT_SECTION_KEY = '__flat';

export interface DataTableFilterDropdownChromeProps {
    /** Pill text, e.g. "Status". */
    label: string;
    align?: 'left' | 'center' | 'right';
    /**
     * Force the in-menu search box on or off. Defaults to on above 8 options -
     * counted across every section, not per section.
     */
    searchable?: boolean;
    disabled?: boolean;
    /** Classes on the positioning wrapper. */
    className?: string;
    /** Classes on the pill button. */
    buttonClassName?: string;
    /** Optional node rendered below the options list, separated by a top border. */
    footer?: ReactNode;
    /**
     * Toolbar-density variant: the pill sizes to its content instead of a 160px
     * minimum, and hovering an active pill swaps the chevron for a "×" that
     * clears the filter without opening the menu.
     */
    workQueueVariant?: boolean;
    /**
     * Renders the pill as active even with nothing selected, showing a dot in
     * place of the count. For filters whose effect is expressed elsewhere -
     * a footer toggle, say - and would otherwise leave the pill looking inert.
     */
    forceActive?: boolean;
    /** Keeps an invisible count badge while inactive, so selecting doesn't widen the pill. */
    reserveCountSpace?: boolean;
}

export interface DataTableFilterDropdownBaseProps extends DataTableFilterDropdownChromeProps {
    options: readonly FilterOption[];
}

export interface DataTableSingleFilterProps extends DataTableFilterDropdownBaseProps {
    multiple?: false;
    /** The selected value, or `null` for "no filter". */
    value: string | null;
    onChange: (value: string | null) => void;
    sections?: undefined;
}

export interface DataTableMultiFilterProps extends DataTableFilterDropdownBaseProps {
    multiple: true;
    value: readonly string[];
    onChange: (value: string[]) => void;
    sections?: undefined;
}

interface DataTableFilterSectionBase {
    /** Stable identity - the React key. */
    key: string;
    /** Group header shown above this section's options. */
    label: string;
    options: readonly FilterOption[];
}

export interface DataTableSingleFilterSection extends DataTableFilterSectionBase {
    multiple?: false;
    value: string | null;
    onChange: (value: string | null) => void;
}

export interface DataTableMultiFilterSection extends DataTableFilterSectionBase {
    multiple: true;
    value: readonly string[];
    onChange: (value: string[]) => void;
}

/**
 * One labelled group inside a sectioned dropdown - e.g. "States" and "Cities"
 * under one "Location" pill. Each section owns its own value and `onChange`
 * (they typically map to distinct query params) but shares the pill, the search
 * box, the "Clear" action and the combined count badge.
 */
export type DataTableFilterSection = DataTableSingleFilterSection | DataTableMultiFilterSection;

export interface DataTableSectionedFilterProps extends DataTableFilterDropdownChromeProps {
    sections: readonly DataTableFilterSection[];
    options?: undefined;
    multiple?: undefined;
    value?: undefined;
    onChange?: undefined;
}

/**
 * Three interfaces rather than one with widened fields, so `props.sections`
 * discriminates the grouped variant from the flat one and `props.multiple`
 * discriminates single from multi within it. Every caller - and the component
 * itself - stays cast-free, and a flat dropdown cannot accidentally be handed
 * a section's `onChange`.
 */
export type DataTableFilterDropdownProps =
    DataTableSingleFilterProps | DataTableMultiFilterProps | DataTableSectionedFilterProps;

/** The selection half of either a flat dropdown's props or one section's. */
type FilterSelection =
    | { multiple?: false; value: string | null; onChange: (value: string | null) => void }
    | { multiple: true; value: readonly string[]; onChange: (value: string[]) => void };

type FilterSelectionState = Pick<
    ResolvedFilterSection,
    'multiple' | 'selected' | 'closeOnSelect' | 'toggle' | 'clear'
>;

function resolveSelection(spec: FilterSelection): FilterSelectionState {
    if (spec.multiple) {
        const { value, onChange } = spec;
        return {
            multiple: true,
            selected: value,
            closeOnSelect: false,
            toggle: (option) =>
                onChange(
                    value.includes(option)
                        ? value.filter((entry) => entry !== option)
                        : [...value, option]
                ),
            clear: () => onChange([])
        };
    }
    const { value, onChange } = spec;
    return {
        multiple: false,
        selected: value === null ? [] : [value],
        closeOnSelect: true,
        // Re-picking the current value clears it: the only way back to "no filter".
        toggle: (option) => onChange(value === option ? null : option),
        clear: () => onChange(null)
    };
}

function resolveFilterSections(props: DataTableFilterDropdownProps): ResolvedFilterSection[] {
    if (props.sections) {
        return props.sections.map((section) => ({
            key: section.key,
            label: section.label,
            options: section.options,
            ...resolveSelection(section)
        }));
    }
    return [
        {
            key: FLAT_SECTION_KEY,
            label: null,
            options: props.options,
            ...resolveSelection(props)
        }
    ];
}

/**
 * The canonical filter pill: single, multi or grouped selection behind the
 * design system's `rounded-full border-2` pill (`border-gray-300` inactive /
 * `border-gray-700` active or open).
 *
 * Fully controlled and stateless with respect to the filter value - it owns
 * only its own open/search/active-option UI state. Selecting an option calls
 * back; what that means (a query param, a refetch, a debounce) is the app's
 * business.
 */
export function DataTableFilterDropdown(props: DataTableFilterDropdownProps) {
    const {
        label,
        align = 'left',
        disabled = false,
        className,
        buttonClassName,
        footer,
        workQueueVariant = false,
        forceActive = false,
        reserveCountSpace = false
    } = props;

    const [open, setOpen] = useState(false);
    // Trails `open` by one transition so the menu can animate shut with its
    // content still in place, then stops rendering it - a closed pill costs
    // nothing even with several hundred options.
    const [menuMounted, setMenuMounted] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(-1);
    const [hovered, setHovered] = useState(false);

    const rootRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const activeOptionRef = useRef<HTMLButtonElement>(null);
    const baseId = useId();
    const listboxId = `${baseId}-listbox`;
    const optionId = (index: number) => `${baseId}-option-${index}`;

    const sections = resolveFilterSections(props);
    const optionCount = sections.reduce((total, section) => total + section.options.length, 0);
    const searchable = props.searchable ?? optionCount > SEARCHABLE_FROM;
    const groups = menuMounted ? resolveFilterGroups(sections, query) : [];
    const flat = flattenFilterGroups(groups);

    // The distinct union, not a per-section sum: sections may legally share
    // one value array and `onChange` (the patients "Internal step" pill hands
    // the same selection to all nine phase sections), and a sum would multiply
    // each selection by the number of sections repeating it. A flat dropdown
    // (one synthetic section) and genuinely distinct sections - whose option
    // values are disjoint - count identically under either scheme.
    const selectedCount = new Set(sections.flatMap((section) => section.selected)).size;
    const active = selectedCount > 0 || forceActive;
    const multiselectable = sections.some((section) => section.multiple);

    const openMenu = useCallback(() => {
        setMenuMounted(true);
        setOpen(true);
    }, []);

    const closeMenu = useCallback(() => {
        setOpen(false);
        setQuery('');
        setActiveIndex(-1);
    }, []);

    useEffect(() => {
        if (open) {
            return;
        }
        const handle = window.setTimeout(() => setMenuMounted(false), MENU_TRANSITION_MS);
        return () => window.clearTimeout(handle);
    }, [open]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const onPointerDown = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                closeMenu();
            }
        };
        // Also on document, not just the root's onKeyDown: Escape should close
        // the menu even once focus has left the component.
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeMenu();
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, closeMenu]);

    useEffect(() => {
        if (open && searchable) {
            searchRef.current?.focus();
        }
    }, [open, searchable]);

    useEffect(() => {
        activeOptionRef.current?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);

    const selectOption = (section: ResolvedFilterSection, value: string) => {
        section.toggle(value);
        if (section.closeOnSelect) {
            closeMenu();
            buttonRef.current?.focus();
        }
    };

    const clearAll = () => {
        sections.forEach((section) => section.clear());
    };

    const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
        if (disabled) {
            return;
        }
        if (!open) {
            if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openMenu();
            }
            return;
        }
        switch (event.key) {
            case 'Escape':
                event.preventDefault();
                closeMenu();
                buttonRef.current?.focus();
                break;
            case 'ArrowDown':
                event.preventDefault();
                setActiveIndex((previous) => Math.min(previous + 1, flat.length - 1));
                break;
            case 'ArrowUp':
                event.preventDefault();
                setActiveIndex((previous) =>
                    previous < 0 ? flat.length - 1 : Math.max(previous - 1, 0)
                );
                break;
            case 'Enter':
            case ' ': {
                // Space has to keep typing a space inside the search box.
                if (event.key === ' ' && event.target === searchRef.current) {
                    return;
                }
                event.preventDefault();
                const entry = flat[activeIndex];
                if (entry) {
                    selectOption(entry.section, entry.item.value);
                }
                break;
            }
        }
    };

    const showHoverClear = workQueueVariant && selectedCount > 0 && hovered && !open && !disabled;

    return (
        <div
            className={cx('relative inline-block text-left', className)}
            ref={rootRef}
            onKeyDown={handleKeyDown}
        >
            <button
                ref={buttonRef}
                type="button"
                disabled={disabled}
                onClick={() => (open ? closeMenu() : openMenu())}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-controls={listboxId}
                aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
                // `as const` rather than a `CSSProperties` annotation: shared-ui
                // resolves a nested csstype, and an explicit annotation here
                // type-errors in consumers that resolve a different copy.
                style={workQueueVariant ? undefined : PILL_MIN_WIDTH}
                className={dataTableFilterPillClasses({
                    active: open || active,
                    disabled,
                    className: cx(
                        'justify-between',
                        workQueueVariant && 'whitespace-nowrap',
                        buttonClassName
                    )
                })}
            >
                <span className="truncate">{label}</span>
                <span className="flex items-center gap-1">
                    {(active || reserveCountSpace) && (
                        <span
                            aria-hidden={!active}
                            className={cx(
                                'flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-gray-700 text-white text-xs leading-none',
                                !active && 'invisible'
                            )}
                        >
                            {selectedCount > 0 ? (
                                selectedCount
                            ) : (
                                <span
                                    aria-hidden
                                    className="block w-1.5 h-1.5 rounded-full bg-white"
                                />
                            )}
                        </span>
                    )}
                    {showHoverClear ? (
                        // A plain span, not a nested button: interactive content
                        // cannot nest, and the accessible clear path is the
                        // "Clear" action inside the menu.
                        <span
                            aria-hidden
                            className="flex items-center justify-center rounded-full hover:bg-gray-200 cursor-pointer"
                            onClick={(event) => {
                                event.stopPropagation();
                                clearAll();
                            }}
                        >
                            <XMarkIcon className="h-4 w-4 m-0.5" />
                        </span>
                    ) : (
                        <ChevronDownIcon
                            aria-hidden
                            className={cx(
                                'h-5 w-5 transition-transform duration-200',
                                open && 'rotate-180'
                            )}
                        />
                    )}
                </span>
            </button>

            <div
                aria-hidden={!open}
                className={cx(
                    'absolute mt-2 w-64 z-30 bg-white shadow-lg rounded-2xl overflow-hidden',
                    'transition-all duration-300 ease-in-out ring-2',
                    open ? 'ring-gray-700' : 'ring-transparent',
                    ALIGNMENT[align]
                )}
                style={{ maxHeight: open ? MENU_OPEN_MAX_HEIGHT : '0', opacity: open ? 1 : 0 }}
            >
                {menuMounted && (
                    <>
                        <div className="px-3 pt-3 pb-2">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-[10.5px] font-semibold tracking-wide text-gray-400 uppercase">
                                    {label}
                                </span>
                                {selectedCount > 0 && (
                                    <button
                                        type="button"
                                        onClick={clearAll}
                                        className="text-xs text-gray-500 underline hover:text-gray-700 transition-colors"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            {searchable && (
                                <div className="relative mt-2">
                                    <MagnifyingGlassIcon
                                        aria-hidden
                                        className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
                                    />
                                    <input
                                        ref={searchRef}
                                        type="text"
                                        value={query}
                                        onChange={(event) => {
                                            setQuery(event.target.value);
                                            setActiveIndex(-1);
                                        }}
                                        placeholder={`Search ${label.toLowerCase()}…`}
                                        aria-label={`Search ${label}`}
                                        aria-controls={listboxId}
                                        aria-activedescendant={
                                            activeIndex >= 0 ? optionId(activeIndex) : undefined
                                        }
                                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-700"
                                    />
                                </div>
                            )}
                        </div>
                        <FilterDropdownOptionList
                            listboxId={listboxId}
                            label={label}
                            groups={groups}
                            activeIndex={activeIndex}
                            optionId={optionId}
                            onSelect={selectOption}
                            activeOptionRef={activeOptionRef}
                            multiselectable={multiselectable}
                        />
                        {footer != null && <div className="border-t border-gray-100">{footer}</div>}
                    </>
                )}
            </div>
        </div>
    );
}
